<template>
    <div class="grid grid-cols-24 mb-24">
        <div
            class="col-start-1 col-span-24 row-span-2 row-start-1 z-0 relative"
        >
            <div class="bg-video">
                <video autoplay muted loop playsinline>
                    <source :src="video" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            </div>
        </div>

        <div
            class="row-start-1 row-span-1 col-start-3 col-end-23 md:col-end-12 color-white py-16 z-10"
        >
            <h1 class="h1 text-white leading-none">{{ title }}</h1>
        </div>

        <div class="col-start-3 col-end-23 row-span-2 row-start-2 pt-8 z-30">
            <SearchHeader
                v-model="searchInputValue"
                :suggestions="suggestions"
                :search-title="searchTitle"
                :search-placeholder="searchPlaceholder"
                :free-text-order-button-text="freeTextOrderButtonText"
                :exact-cas="exactCas"
                @submit="handleFormSubmit($event)"
                @input="fetchAutocompleteSuggestions"
                @update:exactCas="exactCas = $event"
                @scroll-down="fetchMoreAutocompleteSuggestions"
            />
        </div>

        <div class="col-start-3 col-end-23 pt-8">
            <nav class="text-f5 flex gap-4 lg:gap-8 items-center flex-wrap">
                <a href="/">
                    <i class="far fa-home"></i>
                </a>
                <span class="font-bold">Products</span>
            </nav>
        </div>

        <div class="sui-layout col-start-3 col-end-23 z-20">
            <div v-if="searchState.wasSearched" class="">
                <div class="flex flex-col gap-8 md:gap-16 mb-12 md:mb-24">
                    <div class="mt-8 md:mt-16 z-20">
                        <div class="flex justify-end mb-2 md:mb-4 h-7">
                            <button
                                class="btn-cb btn-small"
                                v-if="Object.keys(appliedFilters).length > 0"
                                @click="resetAllFilters()"
                            >
                                Reset all filters
                                <i class="fas fa-times-circle"></i>
                            </button>
                        </div>
                        <div
                            class="flex flex-row justify-between bg-white px-8 py-3 rounded shadow-md z-30 gap-8 flex-wrap"
                        >
                            <SearchFacet
                                :checked="Name_des_Herstellers"
                                :facet="searchState.facets.Name_des_Herstellers"
                                :facet-title="'Manufacturer'"
                                @change="
                                    handleFacetChange(
                                        $event,
                                        'Name_des_Herstellers',
                                    )
                                "
                                @reset-facet="
                                    resetFacet('Name_des_Herstellers')
                                "
                            />

                            <SearchFacet
                                :checked="Gruppenname"
                                :facet="searchState.facets.Gruppenname"
                                :facet-title="'Group'"
                                @change="
                                    handleFacetChange($event, 'Gruppenname')
                                "
                                @reset-facet="resetFacet('Gruppenname')"
                            />

                            <SearchFacet
                                :checked="Categorie"
                                :facet="searchState.facets.Categorie"
                                :facet-title="'Category'"
                                @change="handleFacetChange($event, 'Categorie')"
                                @reset-facet="resetFacet('Categorie')"
                            />

                            <!-- <SearchFacet
                                :checked="Verpackungsgruppe"
                                :facet="searchState.facets.Verpackungsgruppe"
                                :facet-title="'Packaging group'"
                                @change="
                                    handleFacetChange(
                                        $event,
                                        'Verpackungsgruppe',
                                    )
                                "
                                @reset-facet="resetFacet('Verpackungsgruppe')"
                            /> -->

                            <SearchFacet
                                :checked="VerkaufsME"
                                :facet="searchState.facets.VerkaufsME"
                                :facet-title="'Sales unit'"
                                @change="
                                    handleFacetChange($event, 'VerkaufsME')
                                "
                                @reset-facet="resetFacet('VerkaufsME')"
                            />

                            <SearchFacet
                                :checked="Reinheit"
                                :facet="searchState.facets.Reinheit"
                                :facet-title="'Purity'"
                                @change="handleFacetChange($event, 'Reinheit')"
                                @reset-facet="resetFacet('Reinheit')"
                            />

                            <SearchFacet
                                :checked="Listenpreis"
                                :facet="searchState.facets.Listenpreis"
                                :facet-title="'Price'"
                                @change="
                                    handleFacetChange($event, 'Listenpreis')
                                "
                                @reset-facet="resetFacet('Listenpreis')"
                            />

                            <SearchAvailabilityFilter
                                @change="changeFilterAvailability($event)"
                                :checked="filterAvailability"
                            />
                        </div>
                        <div
                            class="flex flex-wrap gap-2 mt-4"
                            v-if="Object.keys(appliedFilters).length > 0"
                        >
                            <span
                                v-for="(value, key) in appliedFilters"
                                :key="key"
                                class="btn-cb btn-small"
                                @click="resetFacet(key)"
                            >
                                <span
                                    >{{ filterNames[key]
                                    }}<span v-if="value !== 'true'"
                                        >: {{ value }}</span
                                    ></span
                                >
                                <i class="fas fa-times-circle"></i>
                            </span>
                        </div>
                    </div>

                    <div
                        class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 z-10"
                    >
                        <SearchPagingInfo
                            :search-state="searchState"
                            :current-results="currentResults"
                            :searchTerm="searchInputValue"
                        />

                        <SearchSort v-show="thereAreResults" v-model="sortBy" />
                    </div>
                    <div
                        class="relative min-h-[200px]"
                        :class="{
                            'loading-products': searchState.isLoading,
                        }"
                    >
                        <div ref="resultsAnchor">
                            <SearchResults
                                v-show="thereAreResults"
                                :results="searchState.results"
                                :filter-availability="filterAvailability"
                            />
                        </div>

                        <div
                            v-show="searchState.isLoading"
                            class="loading-animation-top z-40 w-full h-full flex justify-center items-center absolute top-0 left-0"
                        ></div>

                        <button
                            @click="loadMore"
                            class="btn-cb mt-14"
                            v-if="
                                thereAreResults &&
                                searchState.totalResults > currentResults
                            "
                        >
                            Load more
                            <i class="fas fa-arrow-down"></i>
                        </button>

                        <div
                            v-if="!thereAreResults && !searchState.isLoading"
                            class="text-left pb-16"
                        >
                            <div>
                                <h2 class="h2 mb-4">No results found</h2>
                                <p
                                    class="text-f5 mb-4"
                                    v-if="
                                        Object.keys(appliedFilters).length > 0
                                    "
                                >
                                    Please try adjusting or removing filters.
                                </p>
                                <p class="text-f5" v-else>
                                    Please try adjusting your search term.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { SearchDriver } from "@elastic/search-ui";
import config from "../../searchConfig";
import SearchResults from "./search-results.vue";
import SearchFacet from "./search-facet.vue";
import SearchHeader from "./search-header.vue";
import SearchPagingInfo from "./search-paging-info.vue";
// import SearchPagination from "./SearchPagination.vue";
import SearchSort from "./search-sort.vue";
import SearchResultsPerPage from "./search-results-per-page.vue";
import SearchAvailabilityFilter from "./search-availability-filter.vue";

const driver = new SearchDriver(config);

export default {
    components: {
        SearchResults,
        SearchFacet,
        SearchHeader,
        SearchPagingInfo,
        // SearchPagination,
        SearchSort,
        SearchResultsPerPage,
        SearchAvailabilityFilter,
    },
    props: {
        image: {
            type: String,
            default: null,
        },
        video: {
            type: String,
            default: null,
        },
        title: {
            type: String,
            default: null,
        },
        searchTitle: {
            type: String,
            default: "Find the items you want from our range",
        },
        searchPlaceholder: {
            type: String,
            default:
                "Please enter the desired search term, item number, or brand name...",
        },
        freeTextOrderButtonText: {
            type: String,
            default: "Create free text orders",
        },
    },
    data() {
        return {
            searchInputValue: "",
            searchState: {},
            Name_des_Herstellers: [],
            Gruppenname: [],
            Categorie: [],
            Verpackungsgruppe: [],
            VerkaufsME: [],
            Reinheit: [],
            Listenpreis: [],
            resultsPerPage: 20,
            currentResults: 20,
            sortBy: "Artikelnr",
            filterNames: {
                Name_des_Herstellers: "Manufacturer",
                Gruppenname: "Group",
                Categorie: "Category",
                Verpackungsgruppe: "Packaging group",
                VerkaufsME: "Sales unit",
                Reinheit: "Purity",
                Listenpreis: "Price",
                availability: "Availability",
                ExactCas: "Search exact CAS-Number",
            },
            filterAvailability: false,
            suggestions: [],
            exactCas: false,
            pendingScrollToResults: false,
        };
    },
    computed: {
        thereAreResults() {
            return (
                this.searchState.totalResults &&
                this.searchState.totalResults > 0
            );
        },
        appliedFilters() {
            const applied = {};
            if (
                this.searchState.filters &&
                this.searchState.filters.length > 0
            ) {
                this.searchState.filters.forEach((filter) => {
                    let values = [];
                    filter.values.forEach((value) => {
                        if (
                            this.searchState.facets[filter.field] &&
                            this.searchState.facets[filter.field].length > 0 &&
                            this.searchState.facets[filter.field][0].type ===
                                "range"
                        ) {
                            values.push(value.name);
                        } else {
                            values.push(value);
                        }
                    });
                    applied[filter.field] = values.join(", ");
                });
            }
            return applied;
        },
    },
    watch: {
        resultsPerPage(newResultsPerPage) {
            driver.setResultsPerPage(newResultsPerPage);
        },
        sortBy(newSortBy) {
            driver.setSort(newSortBy, "asc");
        },
    },
    mounted() {
        const { searchTerm, sortField, resultsPerPage, filters, facets } =
            driver.getState();

        // restoring UI from url query
        this.searchInputValue = searchTerm;
        this.sortBy = sortField;
        this.resultsPerPage = resultsPerPage;
        filters.forEach((filter) => {
            if (
                facets[filter.field] &&
                facets[filter.field].length > 0 &&
                facets[filter.field][0].type === "range"
            ) {
                this[filter.field] = filter.values.map((value) => value.name);
            } else {
                this[filter.field] = filter.values;
            }
        });

        driver.subscribeToStateChanges((state) => {
            this.searchState = state;

            if (
                this.pendingScrollToResults &&
                !state.isLoading &&
                state.results?.length > 0
            ) {
                this.pendingScrollToResults = false;

                this.$nextTick(() => {
                    this.scrollToResults();
                });
            }
        });

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);

        if (urlParams.has("q") && urlParams.get("q")) {
            this.pendingScrollToResults = true;
        }

        for (const [key, value] of urlParams) {
            if (value === "availability") {
                this.filterAvailability = true;
            }
            if (value === "ExactCas") {
                this.exactCas = true;
            }
        }
    },
    methods: {
        handleFormSubmit(payload = {}) {
            if (payload.searchTerm !== undefined) {
                this.searchInputValue = payload.searchTerm;
            }

            if (typeof payload.exactCas === "boolean") {
                this.exactCas = payload.exactCas;
            }

            this.pendingScrollToResults =
                payload.shouldScrollToResults === true;

            if (this.exactCas) {
                // add filter
                driver.addFilter("ExactCas", this.exactCas, "all");
            } else {
                // remove filter
                driver.removeFilter("ExactCas");
            }

            if (this.filterAvailability) {
                // add filter
                driver.addFilter("availability", true, "all");
            } else {
                // remove filter
                driver.removeFilter("availability");
            }

            // Get current state including filters before changing search term
            const currentState = driver.getState();
            const currentFilters = currentState.filters;

            // Set the new search term
            driver.getActions().setSearchTerm(this.searchInputValue);

            // Re-apply all existing filters after search term change
            if (currentFilters && currentFilters.length > 0) {
                currentFilters.forEach((filter) => {
                    filter.values.forEach((value) => {
                        driver.addFilter(filter.field, value, filter.type);
                    });
                });
            }
        },
        handleFacetChange(event, facet) {
            const { value, checked } = event.target;
            const facetFromDriver = driver.getState().facets[facet];
            const valueforApi =
                facetFromDriver.type === "range"
                    ? facetFromDriver.data.find(
                          (item) => item.value.name === value,
                      ).value
                    : value;

            if (checked) {
                this[facet].push(value);
                driver.addFilter(facet, valueforApi, "any");
            } else {
                const index = this[facet].indexOf(value);
                if (index > -1) {
                    this[facet].splice(index, 1);
                }
                driver.removeFilter(facet, valueforApi, "any");
            }
        },
        resetFacet(facet) {
            this[facet] = [];
            driver.removeFilter(facet);

            if (facet === "availability") {
                this.filterAvailability = false;
            }
            if (facet === "ExactCas") {
                this.exactCas = false;
            }
        },
        setCurrentPage(page) {
            driver.setCurrent(page);
        },
        loadMore() {
            this.currentResults = this.currentResults + this.resultsPerPage;
            driver.setResultsPerPage(this.currentResults);
        },
        fetchAutocompleteSuggestions(searchTerm) {
            if (!searchTerm) {
                this.suggestions = [];
                return;
            }

            driver.getActions().setSearchTerm(searchTerm, {
                refresh: false,
                autocompleteResults: true,
            });

            driver.subscribeToStateChanges((state) => {
                if (state.autocompletedResults) {
                    this.suggestions = state.autocompletedResults.map(
                        (result) => result.Artikelbeschreibung.raw, // Adjust field name based on your schema
                    );
                }
            });
        },
        fetchMoreAutocompleteSuggestions(searchTerm) {
            if (!searchTerm) {
                this.suggestions = [];
                return;
            }

            const additionalSuggestionsCount = 10;
            const newResultsPerPage =
                this.suggestions.length + additionalSuggestionsCount;

            // Direct API call to fetch more autocomplete results
            fetch(`/actions/search/elastic/autocomplete`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    state: {
                        searchTerm: searchTerm,
                    },
                    queryConfig: {
                        results: {
                            resultsPerPage: newResultsPerPage,
                        },
                    },
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    this.suggestions = data.autocompletedResults.map(
                        (result) => result.Artikelbeschreibung.raw, // Adjust field name based on your schema
                    );
                })
                .catch((error) => {
                    console.error(
                        "Error fetching more autocomplete suggestions:",
                        error,
                    );
                });
        },

        scrollToResults() {
            const target = this.$refs.resultsAnchor;

            if (!target) {
                return;
            }

            const headerOffset = window.matchMedia("(min-width: 1024px)")
                .matches
                ? 150
                : 75;
            const gap = 16;
            const targetTop =
                target.getBoundingClientRect().top +
                window.scrollY -
                headerOffset -
                gap;

            window.scrollTo({
                top: targetTop,
                behavior: "smooth",
            });
        },
        resetAllFilters() {
            const facets = Object.keys(this.searchState.facets);
            facets.forEach((facet) => {
                this.resetFacet(facet);
            });
            this.filterAvailability = false;
            this.exactCas = false;
            this.handleFormSubmit();
        },
        changeFilterAvailability(event) {
            this.filterAvailability = event.target.checked;
            this.handleFormSubmit();
        },
    },
};
</script>
